import {useContext} from 'react';

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {Box, IconButton, Typography} from '@mui/material';

import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {Department} from '../../../profile/types';

export interface Props {
  departments: Department[];
  selectedDepartmentId: string | null;
  emptyLabel: string;
  addLabel: string;
  onSelect: (departmentId: string) => void;
  onAdd: () => void;
  onEdit: (department: Department) => void;
  onDelete: (department: Department) => void;
}

export const CompanyDepartmentList = ({
  departments,
  selectedDepartmentId,
  emptyLabel,
  addLabel,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
}: Props) => {
  const currentLanguage = useContext(LanguageContext);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        height: '100%',
      }}
    >
      {departments.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{py: 1}}>
          {emptyLabel}
        </Typography>
      ) : (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
          {departments.map((department) => {
            const isSelected = selectedDepartmentId === department.resourceId;

            return (
              <Box
                key={department.resourceId}
                onClick={() => onSelect(department.resourceId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  bgcolor: isSelected ? greenColors[50] : 'transparent',
                  border: 1,
                  borderColor: isSelected ? greenColors[4] : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? greenColors[50] : 'action.hover',
                  },
                }}
              >
                <Typography variant='body1' sx={{flex: 1, minWidth: 0}}>
                  {department.name[currentLanguage]}
                </Typography>
                <IconButton
                  aria-label='edit department'
                  size='small'
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(department);
                  }}
                >
                  <EditOutlinedIcon fontSize='small' />
                </IconButton>
                <IconButton
                  aria-label='delete department'
                  size='small'
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(department);
                  }}
                >
                  <DeleteOutlinedIcon fontSize='small' />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{pt: 2}}>
        <EmpionButton variant='secondary' size='small' onClick={onAdd}>
          {addLabel}
        </EmpionButton>
      </Box>
    </Box>
  );
};
